import { ReactNode, useState, createContext, useContext, Children, isValidElement, cloneElement } from 'react'
import { twMerge } from 'tailwind-merge'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// ============================================================================
// Tabs Context
// ============================================================================
interface TabsContextValue {
  activeTab: string
  setActiveTab: (key: string) => void
}

const TabsContext = createContext<TabsContextValue | null>(null)

const useTabsContext = () => {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component')
  }
  return context
}

// ============================================================================
// Tabs Container
// ============================================================================
interface TabsProps {
  children: ReactNode
  defaultTab?: string
  className?: string
  variant?: 'underline' | 'pills' | 'boxed'
  onChange?: (tabKey: string) => void
}

export const Tabs = ({ 
  children, 
  defaultTab, 
  className, 
  variant = 'underline',
  onChange 
}: TabsProps) => {
  // Extract tab keys from children to set default
  const childArray = Children.toArray(children)
  const firstTabKey = childArray.length > 0 && isValidElement(childArray[0])
    ? (childArray[0].props as TabProps).tabKey
    : ''

  const [activeTab, setActiveTab] = useState(defaultTab || firstTabKey)

  const handleTabChange = (key: string) => {
    setActiveTab(key)
    onChange?.(key)
  }

  // Separate tab headers from content
  const tabs: Array<{
    tabKey: string
    title: string
    icon?: IconDefinition
    badge?: number | string
    disabled?: boolean
  }> = []

  Children.forEach(children, (child) => {
    if (isValidElement(child) && (child.type as any).displayName === 'Tab') {
      const props = child.props as TabProps
      tabs.push({
        tabKey: props.tabKey,
        title: props.title,
        icon: props.icon,
        badge: props.badge ?? props.length,
        disabled: props.disabled
      })
    }
  })

  const variantStyles = {
    underline: {
      container: 'border-b border-slate-200',
      tabList: 'flex gap-1 -mb-px',
      tab: 'px-4 py-3 text-sm font-medium transition-all duration-200 border-b-2 border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
      activeTab: 'text-indigo-600 border-indigo-600 hover:text-indigo-600 hover:border-indigo-600'
    },
    pills: {
      container: 'bg-slate-100 p-1 rounded-xl inline-flex',
      tabList: 'flex gap-1',
      tab: 'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 text-slate-600 hover:text-slate-900',
      activeTab: 'bg-white text-slate-900 shadow-sm'
    },
    boxed: {
      container: 'bg-slate-50 border border-slate-200 p-1.5 rounded-xl',
      tabList: 'flex gap-1',
      tab: 'px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 text-slate-500 hover:text-slate-700 hover:bg-slate-100',
      activeTab: 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
    }
  }

  const styles = variantStyles[variant]

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleTabChange }}>
      <div className={twMerge('space-y-6', className)}>
        {/* Tab Headers */}
        <div className={styles.container}>
          <div className={styles.tabList} role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.tabKey}
                role="tab"
                aria-selected={activeTab === tab.tabKey}
                disabled={tab.disabled}
                onClick={() => !tab.disabled && handleTabChange(tab.tabKey)}
                className={twMerge(
                  styles.tab,
                  activeTab === tab.tabKey && styles.activeTab,
                  tab.disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className="flex items-center gap-2">
                  {tab.icon && (
                    <FontAwesomeIcon icon={tab.icon} className="text-sm" />
                  )}
                  <span>{tab.title}</span>
                  {tab.badge !== undefined && tab.badge !== 0 && (
                    <span className={twMerge(
                      'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full',
                      activeTab === tab.tabKey
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-slate-200 text-slate-600'
                    )}>
                      {tab.badge}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div role="tabpanel">
          {Children.map(children, (child) => {
            if (isValidElement(child) && (child.type as any).displayName === 'Tab') {
              const props = child.props as TabProps
              if (props.tabKey === activeTab) {
                return child
              }
            }
            return null
          })}
        </div>
      </div>
    </TabsContext.Provider>
  )
}

// ============================================================================
// Tab Panel
// ============================================================================
interface TabProps {
  children: ReactNode
  tabKey: string
  title: string
  icon?: IconDefinition
  badge?: number | string
  length?: number // alias for badge
  disabled?: boolean
  className?: string
}

export const Tab = ({ children, className }: TabProps) => {
  return (
    <div className={twMerge('animate-in fade-in-50 duration-200', className)}>
      {children}
    </div>
  )
}

Tab.displayName = 'Tab'

export default Tabs

